import { useState, useRef } from 'react';
import DataTable from './DataTable';
import { theme } from '../theme';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Nombre' },
  { key: 'type', label: 'Tipo' },
];

const TYPES = [
  { v: 'texto', label: 'Texto' },
  { v: 'imagen', label: 'Imagen' },
  { v: 'video', label: 'Video' },
  { v: 'documento', label: 'Documento' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
};

const overlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem',
};

// Previsualiza el contenido según su tipo: texto, imagen, video o documento.
function ContentPreview({ type, description }) {
  if (!description) return <div style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Sin contenido aún.</div>;
  if (type === 'imagen') {
    return <img src={description} alt="" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, display: 'block' }} />;
  }
  if (type === 'video') {
    return <video src={description} controls style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, display: 'block' }} />;
  }
  if (type === 'documento') {
    return <a href={description} target="_blank" rel="noreferrer" style={{ color: theme.colors.primary, wordBreak: 'break-all' }}>📄 Abrir documento</a>;
  }
  return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: theme.colors.text, fontSize: '0.9rem' }}>{description}</div>;
}

function Archives({ token }) {
  const tableRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState('');
  const authH = { Authorization: `Bearer ${token}` };

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear; objeto = editar
  const [name, setName] = useState('');
  const [type, setType] = useState('texto');
  const [description, setDescription] = useState(''); // texto a enviar O url de media
  const [uploading, setUploading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setType('texto');
    setDescription('');
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || '');
    setType(row.type || 'texto');
    setDescription(row.description || '');
    setFormError('');
    setFormOpen(true);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/archives/upload', { method: 'POST', headers: authH, body: fd });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Error al subir el archivo'); return; }
      setDescription(data.url);
    } catch {
      setFormError('Error de conexión al subir');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setFormError('El nombre es requerido'); return; }
    if (!description.trim()) { setFormError(type === 'texto' ? 'El texto es requerido' : 'Sube un archivo'); return; }
    setFormError('');
    setFormLoading(true);
    try {
      const url = editing ? `/api/archives/${editing.id}` : '/api/archives';
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify({ name: name.trim(), type, description: description.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Error al guardar'); return; }
      setFormOpen(false);
      showSuccess(editing ? 'Archivo actualizado.' : 'Archivo creado.');
      tableRef.current?.reload();
    } catch {
      setFormError('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar el archivo "${row.name}"?`)) return;
    try {
      const res = await fetch(`/api/archives/${row.id}`, { method: 'DELETE', headers: authH });
      const data = await res.json();
      if (!res.ok) { showSuccess(data.message || 'Error al eliminar'); return; }
      showSuccess('Archivo eliminado.');
      tableRef.current?.reload();
    } catch {
      showSuccess('Error de conexión');
    }
  };

  const actionBtn = (color) => ({
    padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${color}`,
    borderRadius: '6px', color, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  });

  const canSubmit = !formLoading && !uploading && name.trim() && description.trim();

  return (
    <main style={{ padding: '2rem 1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>Archivo</h1>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>Tus textos y media rápidos. Solo tú ves y gestionas los tuyos.</p>
          </div>
          <button
            onClick={openCreate}
            style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
          >
            + Crear
          </button>
        </div>

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.colors.success, fontSize: '0.9rem' }}>
            {successMsg}
          </div>
        )}

        <DataTable
          ref={tableRef}
          columns={COLUMNS}
          fetchUrl="/api/archives"
          token={token}
          renderActions={(row) => (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={() => openEdit(row)} style={actionBtn(theme.colors.primary)}>Ver / editar</button>
              <button onClick={() => handleDelete(row)} style={actionBtn(theme.colors.error)}>Eliminar</button>
            </div>
          )}
        />
      </div>

      {formOpen && (
        <div onClick={() => !formLoading && setFormOpen(false)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>{editing ? `Archivo #${editing.id}` : 'Nuevo archivo'}</h2>
              <button onClick={() => !formLoading && setFormOpen(false)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {editing && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Previsualización</label>
                <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '0.75rem', backgroundColor: theme.colors.background }}>
                  <ContentPreview type={type} description={description} />
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Nombre</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} maxLength={255} placeholder="ej: Saludo, Catálogo, Tutorial" autoFocus />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Tipo</label>
              <select value={type} onChange={(e) => { setType(e.target.value); setDescription(''); }} style={{ ...inputStyle, cursor: 'pointer' }}>
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>{type === 'texto' ? 'Texto a enviar' : 'Archivo'}</label>
              {type === 'texto' ? (
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }} placeholder="Escribe el texto que se enviará" />
              ) : (
                <div>
                  <input type="file" onChange={(e) => upload(e.target.files[0])} />
                  {uploading && <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem', marginTop: '0.4rem' }}>Subiendo…</div>}
                  {description && !uploading && (
                    <div style={{ marginTop: '0.6rem' }}>
                      <div style={{ color: theme.colors.success, fontSize: '0.8rem', wordBreak: 'break-all', marginBottom: '0.5rem' }}>{description}</div>
                      <ContentPreview type={type} description={description} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {formError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{formError}</p>}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setFormOpen(false)} disabled={formLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cerrar</button>
              <button onClick={handleSubmit} disabled={!canSubmit} style={{ flex: 1, padding: '10px', backgroundColor: !canSubmit ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: !canSubmit ? 'not-allowed' : 'pointer' }}>{formLoading ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Archives;
